/**
 * Canonical Next.js Data Cache tags for Statamic GraphQL fetches.
 *
 * Keep in sync with CMS: app/Support/CacheTags.php
 * Phase A: collection/taxonomy-level tags only (entry-level tags = Phase B).
 */

export const CACHE_TAGS = Object.freeze({
  pages: "pages",
  events: "events",
  showbags: "showbags",
  rides: "rides",
  food_outlets: "food_outlets",
  competitions: "competitions",
  commercial_exhibitors: "commercial_exhibitors",
  news: "news",
  ads: "ads",
  banners: "banners",
  themes: "themes",
  globals: "globals",
  navs: "navs",
  locations: "locations",
  facilities: "facilities",
  weather: "weather",
});

/** Collection handle → cache tag (must match Statamic collection handles). */
export const COLLECTION_TAGS = Object.freeze({
  pages: CACHE_TAGS.pages,
  events: CACHE_TAGS.events,
  showbags: CACHE_TAGS.showbags,
  rides: CACHE_TAGS.rides,
  food_outlets: CACHE_TAGS.food_outlets,
  competitions: CACHE_TAGS.competitions,
  commercial_exhibitors: CACHE_TAGS.commercial_exhibitors,
  news: CACHE_TAGS.news,
  ads: CACHE_TAGS.ads,
  banners: CACHE_TAGS.banners,
  themes: CACHE_TAGS.themes,
});

/** Taxonomy handle → primary cache tag. */
export const TAXONOMY_TAGS = Object.freeze({
  locations: CACHE_TAGS.locations,
  facilities: CACHE_TAGS.facilities,
});

/** Tags for the sitemap GraphQL query (all collections it unions). */
export const SITEMAP_TAGS = Object.freeze([
  CACHE_TAGS.pages,
  CACHE_TAGS.events,
  CACHE_TAGS.showbags,
  CACHE_TAGS.rides,
  CACHE_TAGS.food_outlets,
  CACHE_TAGS.competitions,
  CACHE_TAGS.commercial_exhibitors,
  CACHE_TAGS.news,
]);

/** Tags that should also bust App Router layout / shared shells. */
export const LAYOUT_BUST_TAGS = Object.freeze([
  CACHE_TAGS.pages,
  CACHE_TAGS.globals,
  CACHE_TAGS.navs,
]);

/** Tags that should bust the map route shell. */
export const MAP_BUST_TAGS = Object.freeze([
  // The map route is gated by settings.enable_map. A global settings save must
  // invalidate its cached 404 as well as its map-data updates.
  CACHE_TAGS.globals,
  CACHE_TAGS.locations,
  CACHE_TAGS.facilities,
]);

/**
 * Locations terms are referenced across many listing UIs — keep the broad
 * invalidation set until Phase B surgical tags.
 */
export const LOCATIONS_QUERY_TAGS = Object.freeze([
  CACHE_TAGS.locations,
  CACHE_TAGS.events,
  CACHE_TAGS.pages,
  CACHE_TAGS.rides,
  CACHE_TAGS.showbags,
  CACHE_TAGS.food_outlets,
  CACHE_TAGS.competitions,
]);

export const FACILITIES_QUERY_TAGS = Object.freeze([
  CACHE_TAGS.facilities,
  CACHE_TAGS.pages,
]);

export const SETTINGS_QUERY_TAGS = Object.freeze([
  CACHE_TAGS.globals,
  CACHE_TAGS.navs,
]);

export const COMPETITIONS_QUERY_TAGS = Object.freeze([
  CACHE_TAGS.competitions,
  CACHE_TAGS.events,
]);

export const MYSHOW_QUERY_TAGS = Object.freeze([
  CACHE_TAGS.showbags,
  CACHE_TAGS.events,
  CACHE_TAGS.rides,
  CACHE_TAGS.food_outlets,
]);

export function tagForCollection(collection) {
  return COLLECTION_TAGS[collection] ?? collection;
}

/** Every tag the frontend is allowed to attach to fetchClient. */
export function allKnownFrontendTags() {
  return Object.values(CACHE_TAGS);
}
